/**
 * API client for communicating with the backend
 * Handles authentication, error handling, request/response formatting, and offline mode
 *
 * Features:
 * - Online/offline detection
 * - Automatic fallback to cached data when offline
 * - Request queueing when offline
 * - Auto-sync when connection restored
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios'
import { offlineCache } from './offlineCache'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:13000/api/v1'
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000')

export interface APIClientListener {
  onOnline?: () => void
  onOffline?: () => void
  onSyncStart?: () => void
  onSyncComplete?: () => void
  onSyncError?: (error: Error) => void
}

class APIClient {
  private axiosInstance: AxiosInstance
  private token: string | null = null
  private isOnline: boolean = navigator.onLine
  private isSyncing: boolean = false
  private listeners: APIClientListener[] = []

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor for adding auth token
    this.axiosInstance.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`
      }
      return config
    })

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - clear token and redirect to login
          this.clearToken()
          // Dispatch logout action or redirect
        }
        return Promise.reject(error)
      }
    )

    // Setup online/offline detection
    this.setupOfflineDetection()
  }

  /**
   * Setup online/offline event listeners
   */
  private setupOfflineDetection(): void {
    // Initial state
    this.isOnline = navigator.onLine

    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.handleOnline()
    })

    window.addEventListener('offline', () => {
      this.handleOffline()
    })

    // Periodic connectivity check every 30 seconds
    setInterval(() => {
      this.checkConnectivity()
    }, 30000)
  }

  /**
   * Check connectivity by pinging the API
   */
  private async checkConnectivity(): Promise<void> {
    if (this.isOnline) {
      try {
        // Try a lightweight health check
        await axios.get(`${API_BASE_URL.replace('/api/v1', '')}/health`, {
          timeout: 5000,
        })
      } catch {
        // If health check fails, assume offline
        if (this.isOnline) {
          this.handleOffline()
        }
      }
    }
  }

  /**
   * Handle coming online
   */
  private handleOnline(): void {
    console.log('🟢 Connection restored - Online')
    this.isOnline = true
    this.notifyListeners('onOnline')

    // Automatically sync queued submissions
    this.autoSync().catch(console.error)
  }

  /**
   * Handle going offline
   */
  private handleOffline(): void {
    console.log('🔴 Lost connection - Offline mode active')
    this.isOnline = false
    this.notifyListeners('onOffline')
  }

  /**
   * Check if currently online
   */
  isConnected(): boolean {
    return this.isOnline
  }

  /**
   * Register a listener for online/offline/sync events
   */
  addListener(listener: APIClientListener): void {
    this.listeners.push(listener)
  }

  /**
   * Remove a listener
   */
  removeListener(listener: APIClientListener): void {
    this.listeners = this.listeners.filter((l) => l !== listener)
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: keyof APIClientListener): void {
    this.listeners.forEach((listener) => {
      const handler = listener[event]
      if (handler) {
        handler()
      }
    })
  }

  /**
   * Automatically sync queued submissions when online
   */
  private async autoSync(): Promise<void> {
    if (this.isSyncing) return

    this.isSyncing = true
    this.notifyListeners('onSyncStart')

    try {
      const submissions = await offlineCache.getQueuedSubmissions()
      console.log(`📤 Syncing ${submissions.length} queued submissions`)

      for (const submission of submissions) {
        try {
          if (submission.type === 'daily_report') {
            // POST the daily report
            await this.axiosInstance.post(
              `/operations/${submission.operationId}/daily-reports`,
              submission.data
            )
          } else {
            // Handle other submission types as needed
            console.log(`Syncing submission type: ${submission.type}`)
          }

          // Remove from queue after successful sync
          await offlineCache.removeQueuedSubmission(submission.id)
          console.log(`✅ Synced submission ${submission.id}`)
        } catch (error) {
          // Increment retry count
          await offlineCache.updateQueuedSubmission(submission.id, {
            retries: submission.retries + 1,
            lastError: (error as Error).message,
          })
          console.error(`Failed to sync submission ${submission.id}:`, error)
        }
      }

      this.notifyListeners('onSyncComplete')
      console.log('✅ Sync completed')
    } catch (error) {
      this.notifyListeners('onSyncError', error as Error)
      console.error('Sync error:', error)
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * Manually trigger sync (for testing or explicit user action)
   */
  async manualSync(): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline')
    }
    return this.autoSync()
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<Record<string, number>> {
    return offlineCache.getCacheStats()
  }

  /**
   * Clear all offline cache (for testing/debugging)
   */
  async clearOfflineCache(): Promise<void> {
    return offlineCache.clearAll()
  }

  setToken(token: string): void {
    this.token = token
  }

  clearToken(): void {
    this.token = null
  }

  async get<T>(url: string, config = {}): Promise<AxiosResponse<T>> {
    try {
      return await this.axiosInstance.get<T>(url, config)
    } catch (error) {
      // If offline and this is a GET request, try cached data
      if (!this.isOnline && axios.isAxiosError(error)) {
        console.warn(`Failed to fetch ${url} - returning cached data if available`)
        // Return cached data logic would go here based on URL pattern
      }
      throw error
    }
  }

  async post<T>(url: string, data?: any, config = {}): Promise<AxiosResponse<T>> {
    try {
      return await this.axiosInstance.post<T>(url, data, config)
    } catch (error) {
      // If offline, queue the submission
      if (!this.isOnline && axios.isAxiosError(error)) {
        console.log(`Queueing POST request: ${url}`)
        // Queue logic would go here
      }
      throw error
    }
  }

  async put<T>(url: string, data?: any, config = {}): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config)
  }

  async delete<T>(url: string, config = {}): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config)
  }
}

export const apiClient = new APIClient()
export default apiClient
